import { ApolloQueryResult, gql } from '@apollo/client';
import { UserDTO } from '../dto/user.dto';
import { PlanDTO } from '../dto/plan.dto';
import { plainToClass, classToPlain, plainToInstance } from 'class-transformer';
import { environment } from '../environments/environment';
import { AccountDTO } from '../dto/account.dto';
import { RetentionOfferDTO } from '../dto/retention-offer.dto';

const PLAN_QUERY = `
  id
  name
  price
  postLimit
  memberLimit
  commentLimit
  accountLimit
  storyLimit
  metricsDays
  analyticsDays
  billingCycle
  period
  type
  isApple
  expiredAt
  setLimit
`;

export const USER_QUERY = gql`
  query {
    me {
      id
      firstName
      lastName
      email
      enabled
      pushNotificationEmail
      identityToken
      createdAt
      profile {
        category
        experience
        role
      }
      accounts {
        id
        username
        pic
        type
        isActive
        isOwner
        isDummy
        isShoplink
        isPersonal
        isBusiness
        isPrimary
        isAutoPostEnabled
        isValidPinterestToken
        isFacebookAutoPostEnabled
        isTwitterAutoPostEnabled
        isValidFacebookToken
        isValidTwitterToken
        accountType
        isPlatform
        twitterAccount {
          id
          username
          pic
          twitterId
          name
        }
      }
      workspaces {
        id
        name
        pic
        isOwner
        isActive
        platformAccounts {
          id
          pic
          username
          type
        }
        teamMembers {
          id
          email
          name
          userId
          memberUserId
          workspaceId
        }
        workspaceNotification {
          id
          workspaceId
          autopostError
          autopostSuccess
          postReminder
          marketing
          collaboration
        }
      }
      socialSets {
        id
        workspaceId
        instagramAccountId
        instagramName
        pinterestName
        pinterestId
        snipfeedId
        icon
        workspace {
          id
          isOwner
          name
          isActive
          pic
          isOwner
          platformAccounts {
            id
            pic
            type
            username
            authState
            tokenExpirationDate
          }
        }
      }
      plan(useActiveAccount: false) {
        ${PLAN_QUERY}
      }
    }
  }
`;

const TEAM_MEMBERS_QUERY = gql`
  query {
    members {
      id
      email
    }
  }
`;

export enum UserTypeKeys {
  FETCH_USER_START = 'FETCH_USER_START',
  FETCH_USER_SUCCESS = 'FETCH_USER_SUCCESS',
  FETCH_USER_FAILURE = 'FETCH_USER_FAILURE',
  FETCH_SETTINGS_FAILURE = 'FETCH_SETTINGS_FAILURE',
  FETCH_SETTINGS_SUCCESS = 'FETCH_SETTINGS_SUCCESS',
  UPDATE_USER = 'UPDATE_USER',
  FETCHING_TEAM_MEMBERS_START = 'FETCHING_TEAM_MEMBERS_START',
  FETCHING_TEAM_MEMBERS_SUCCESS = 'FETCHING_TEAM_MEMBERS_SUCCESS',
  FETCHING_TEAM_MEMBERS_FAILURE = 'FETCHING_TEAM_MEMBERS_FAILURE',
  FETCH_RETENTION_OFFER_START = 'FETCH_RETENTION_OFFER_START',
  FETCH_RETENTION_OFFER_SUCCESS = 'FETCH_RETENTION_OFFER_SUCCESS',
  FETCH_RETENTION_OFFER_FAILURE = 'FETCH_RETENTION_OFFER_FAILURE'
}

export interface FetchUserStartAction {
  type: UserTypeKeys.FETCH_USER_START;
}

export interface FetchUserSuccessAction {
  type: UserTypeKeys.FETCH_USER_SUCCESS;
  user: UserDTO;
  plans: PlanDTO[];
  currentAccount: AccountDTO;
  shoplinkPlans: PlanDTO[];
}

export interface FetchUserFailureAction {
  type: UserTypeKeys.FETCH_USER_FAILURE;
  error: string;
}

export interface FetchSettingsSuccessAction {
  type: UserTypeKeys.FETCH_SETTINGS_SUCCESS;
  settings: Object;
}

export interface FetchSettingsFailureAction {
  type: UserTypeKeys.FETCH_SETTINGS_FAILURE;
  error: string;
}

export interface FetchTeamMembersStart {
  type: UserTypeKeys.FETCHING_TEAM_MEMBERS_START;
}

export interface FetchTeamMembersFailure {
  type: UserTypeKeys.FETCHING_TEAM_MEMBERS_FAILURE;
  error: string;
}

export interface FetchTeamMembersSuccess {
  type: UserTypeKeys.FETCHING_TEAM_MEMBERS_SUCCESS;
  teamMembers: UserDTO[];
}

export type UserActions =
  | FetchUserStartAction
  | FetchUserFailureAction
  | FetchUserSuccessAction
  | FetchSettingsSuccessAction
  | FetchSettingsFailureAction
  | FetchTeamMembersStart
  | FetchTeamMembersFailure
  | FetchTeamMembersSuccess;

export const fetchTeamMembers = () => async (dispatch, getState) => {
  dispatch({
    type: UserTypeKeys.FETCHING_TEAM_MEMBERS_START
  });

  try {
    const response: ApolloQueryResult<any> = await getState().graphClient.query({
      query: TEAM_MEMBERS_QUERY,
      fetchPolicy: 'network-only'
    });

    dispatch({
      type: UserTypeKeys.FETCHING_TEAM_MEMBERS_SUCCESS,
      teamMembers: plainToClass<UserDTO, Object>(UserDTO, response.data.members)
    });
  } catch (e) {
    dispatch({
      type: UserTypeKeys.FETCHING_TEAM_MEMBERS_FAILURE,
      error: e.message
    });
  }
};

export const getSettings = () => async (dispatch, getState) => {
  try {
    const response: ApolloQueryResult<any> = await getState().graphClient.query({
      query: gql`
        query {
          settings
        }
      `,
      fetchPolicy: 'network-only'
    });

    dispatch({
      type: UserTypeKeys.FETCH_SETTINGS_SUCCESS,
      settings: response.data.settings
    });
  } catch (e) {
    dispatch({
      type: UserTypeKeys.FETCH_SETTINGS_FAILURE,
      error: e.message
    });
  }
};

export const fetchUser =
  ({ source = '', useSCPAuth = false } = {}) =>
  async (dispatch, getState) => {
    dispatch({
      type: UserTypeKeys.FETCH_USER_START
    });

    // This query is set up to automatically retry 3x to ensure newly registered users aren't getting prematurely redirected
    //   because the API is not returning 'me' data immediately after registration
    //
    // There is probably a more robust way to handle this but this will ensure mobile onboarding works for now.
    const executeMeQuery = async () => {
      const response: ApolloQueryResult<any> = await getState().graphClient.query({
        query: USER_QUERY,
        fetchPolicy: 'network-only'
      });
      if (response.data.me && response.data.me.id) {
        const user: UserDTO = plainToInstance(UserDTO, response.data.me);
        if (
          user.enabled === false &&
          user.accounts &&
          user.accounts.length &&
          window.location.pathname.indexOf('/registration/confirmation') === -1
        ) {
          window.location.href = '/registration/confirmation';
          return;
        }

        dispatch({
          type: UserTypeKeys.FETCH_USER_SUCCESS,
          user: user,
          currentAccount: user.accounts ? user.accounts.find((account) => account.isActive) : null
        });

        if (source === 'leaveTeam') {
          document.dispatchEvent(
            new CustomEvent('segmentEvent', {
              detail: {
                eventName: 'Profile | Update Team Member',
                properties: { traitsList: ['account'], accounts: user.accounts }
              }
            })
          );
        } else if (source === 'link') {
          const previousAccounts = await getState().user.currentUser.accounts;
          user.accounts.forEach((account) => {
            const sameAccount = previousAccounts.some(
              (element) =>
                element.id === account.id &&
                !!element.isAutoPostEnabled &&
                account.isAutoPostEnabled
            );
            if (sameAccount) {
              document.dispatchEvent(
                new CustomEvent('segmentEvent', {
                  detail: {
                    eventName: 'Profile | Link',
                    accounts: user.accounts.length ? classToPlain(user.accounts) : undefined,
                    properties: {
                      properties: {
                        social: {
                          socialLinkingReferrer: 'activation',
                          socialIGAccountType: account.accountType.toLowerCase(),
                          socialType: 'instagram',
                          sociaIGAuthType: 'facebook'
                        }
                      },
                      traitsList: ['account', 'instagram', 'pinterest', 'twitter'],
                      propertiesList: ['account', 'app', 'device', 'social']
                    }
                  }
                })
              );
            }
          });
        }
      } else {
        console.error(
          'Error occurred fetching user. "me" object not present in response or ID missing.',
          response.data
        );
        throw new Error();
      }
    };

    const handleRetry = (failureCount = 0) => {
      if (failureCount > 2) {
        console.log('Done retrying fetchUser. redirecting to login.');
        window.location.href = useSCPAuth
          ? `${environment.scpUrl}/signin?returnUrl=${environment.siteUrl}`
          : `${environment.planolyUrl}/login?returnUrl=${environment.siteUrl}`;
      } else {
        // wait 2 seconds, and retry
        setTimeout(
          async function () {
            console.log('Retrying fetchUser', failureCount);
            try {
              await executeMeQuery();
            } catch (e) {
              failureCount++;
              handleRetry(failureCount);
            }
          }.bind(this),
          2000
        );
      }
    };

    try {
      await executeMeQuery();
    } catch (e) {
      console.error('Error occurred fetching user.', e);
      handleRetry();
    }
  };

export const setRetentionOffer = (offer: RetentionOfferDTO) => (dispatch) => {
  dispatch({
    type: UserTypeKeys.FETCH_RETENTION_OFFER_SUCCESS,
    offer: offer
  });
};
